import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, Heart, TrendingUp, FileText, AlertCircle, Calendar, Plus, BarChart3 } from 'lucide-react';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '../App';
import { toast, Toaster } from 'sonner';
import ConditionLogForm from '../components/chronic-disease/ConditionLogForms';
import ConditionCharts from '../components/chronic-disease/ConditionCharts';

const ChronicDiseaseHub = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [activeCondition, setActiveCondition] = useState(null);
  const [userConditions, setUserConditions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAddLog, setShowAddLog] = useState(false);
  const [prediction, setPrediction] = useState(null);
  const [report, setReport] = useState(null);

  const [diabetesForm, setDiabetesForm] = useState({ blood_sugar: '', insulin_units: '', carbs: '', meal_type: 'breakfast', notes: '' });
  const [hypertensionForm, setHypertensionForm] = useState({ systolic: '', diastolic: '', heart_rate: '', medication_taken: false, notes: '' });
  const [asthmaForm, setAsthmaForm] = useState({ inhaler_puffs: '', peak_flow: '', triggers: [], symptom_severity: 'none', notes: '' });
  const [arthritisForm, setArthritisForm] = useState({ pain_level: 5, affected_joints: [], stiffness_duration: '', weather_conditions: '', notes: '' });
  const [ibsForm, setIbsForm] = useState({ symptom_type: 'cramping', severity: 5, food_triggers: [], stress_level: 5, notes: '' });

  const conditions = [
    { id: 'diabetes', name: t('chronicDisease.conditions.diabetes'), icon: '💉', color: 'blue' },
    { id: 'hypertension', name: t('chronicDisease.conditions.hypertension'), icon: '❤️', color: 'red' },
    { id: 'asthma', name: t('chronicDisease.conditions.asthma'), icon: '🫁', color: 'cyan' },
    { id: 'arthritis', name: t('chronicDisease.conditions.arthritis'), icon: '🦴', color: 'orange' },
    { id: 'ibs', name: t('chronicDisease.conditions.ibsCrohns'), icon: '🔬', color: 'purple' }
  ];

  useEffect(() => { fetchUserConditions(); }, []);
  useEffect(() => { if (activeCondition) { fetchLogs(activeCondition); fetchStatistics(activeCondition); } }, [activeCondition]);

  const fetchUserConditions = async () => {
    try {
      const r = await axios.get(`${API}/chronic-disease/conditions`);
      setUserConditions(r.data.conditions || []);
      if (r.data.conditions?.length > 0) setActiveCondition(r.data.conditions[0].condition_type);
    } catch {}
  };

  const fetchLogs = async (condition) => {
    setLoading(true);
    try { const r = await axios.get(`${API}/chronic-disease/logs/${condition}?limit=30`); setLogs(r.data.logs || []); }
    catch { toast.error('Failed to load logs'); }
    finally { setLoading(false); }
  };

  const fetchStatistics = async (condition) => {
    try { const r = await axios.get(`${API}/chronic-disease/statistics/${condition}?days=30`); setStatistics(r.data.statistics || {}); } catch {}
  };

  const handleAddLog = async () => {
    let logData = {};
    switch (activeCondition) {
      case 'diabetes': if (!diabetesForm.blood_sugar) { toast.error('Blood sugar is required'); return; } logData = diabetesForm; break;
      case 'hypertension': if (!hypertensionForm.systolic || !hypertensionForm.diastolic) { toast.error('Blood pressure readings are required'); return; } logData = hypertensionForm; break;
      case 'asthma': if (!asthmaForm.inhaler_puffs) { toast.error('Inhaler puffs are required'); return; } logData = asthmaForm; break;
      case 'arthritis': logData = arthritisForm; break;
      case 'ibs': logData = ibsForm; break;
      default: return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/chronic-disease/log`, { condition_type: activeCondition, log_data: logData });
      toast.success('Log added successfully!'); setShowAddLog(false); resetForms();
      fetchLogs(activeCondition); fetchStatistics(activeCondition); fetchUserConditions();
    } catch { toast.error('Failed to add log'); }
    finally { setLoading(false); }
  };

  const handlePredictFlare = async () => {
    if (logs.length < 3) { toast.error('Need at least 3 logs for prediction'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/chronic-disease/predict-flare`, { condition_type: activeCondition, recent_logs: logs.slice(0, 30).map(l => l.log_data) });
      setPrediction(r.data.prediction); toast.success('Flare prediction generated!');
    } catch { toast.error('Failed to generate prediction'); }
    finally { setLoading(false); }
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/chronic-disease/generate-report?condition_type=${activeCondition}&days=30`);
      setReport(r.data.report); toast.success('Doctor report generated!');
    } catch { toast.error('Failed to generate report'); }
    finally { setLoading(false); }
  };

  const resetForms = () => {
    setDiabetesForm({ blood_sugar: '', insulin_units: '', carbs: '', meal_type: 'breakfast', notes: '' });
    setHypertensionForm({ systolic: '', diastolic: '', heart_rate: '', medication_taken: false, notes: '' });
    setAsthmaForm({ inhaler_puffs: '', peak_flow: '', triggers: [], symptom_severity: 'none', notes: '' });
    setArthritisForm({ pain_level: 5, affected_joints: [], stiffness_duration: '', weather_conditions: '', notes: '' });
    setIbsForm({ symptom_type: 'cramping', severity: 5, food_triggers: [], stress_level: 5, notes: '' });
  };

  const prepareChartData = () => {
    if (!logs || logs.length === 0) return [];
    const sortedLogs = [...logs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    switch (activeCondition) {
      case 'diabetes': return sortedLogs.map(l => ({ date: new Date(l.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), bloodSugar: l.log_data.blood_sugar, insulin: l.log_data.insulin_units || 0, carbs: l.log_data.carbs || 0 }));
      case 'hypertension': return sortedLogs.map(l => ({ date: new Date(l.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), systolic: l.log_data.systolic, diastolic: l.log_data.diastolic, heartRate: l.log_data.heart_rate || 0 }));
      case 'asthma': return sortedLogs.map(l => ({ date: new Date(l.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), puffs: l.log_data.inhaler_puffs, peakFlow: l.log_data.peak_flow || 0, severity: { none: 0, mild: 3, moderate: 6, severe: 9 }[l.log_data.symptom_severity] || 0 }));
      case 'arthritis': return sortedLogs.map(l => ({ date: new Date(l.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), painLevel: l.log_data.pain_level, stiffness: l.log_data.stiffness_duration || 0 }));
      case 'ibs': return sortedLogs.map(l => ({ date: new Date(l.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), severity: l.log_data.severity, stress: l.log_data.stress_level || 0 }));
      default: return [];
    }
  };

  const renderStatistics = () => {
    if (!statistics || Object.keys(statistics).length === 0) return <p className="text-gray-500 text-center py-4">No statistics available yet. Start logging data!</p>;
    const statEntries = Object.entries(statistics);
    const numStats = statEntries.length;
    let gridCols = numStats === 2 ? 'grid-cols-2' : numStats === 3 ? 'grid-cols-1 sm:grid-cols-3' : numStats === 4 ? 'grid-cols-2 lg:grid-cols-4' : numStats <= 6 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4';
    return (
      <div className={`grid ${gridCols} gap-3`}>
        {statEntries.map(([key, value]) => {
          const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          if (Array.isArray(value)) {
            return (
              <div key={key} className="col-span-full bg-gradient-to-br from-teal-50 to-cyan-50 p-3 rounded-lg border border-teal-200">
                <p className="text-[10px] font-semibold text-gray-600 mb-2">{formattedKey}</p>
                <div className="space-y-1">{value.slice(0, 3).map(([trigger, count], idx) => (<div key={`s-${idx}`} className="flex justify-between items-center text-xs"><span className="text-gray-700">{trigger}</span><span className="font-bold text-teal-700">{count}x</span></div>))}</div>
              </div>
            );
          }
          let displayValue = typeof value === 'number' ? (value % 1 === 0 ? value : value.toFixed(1)) : value;
          return (
            <div key={key} className="bg-gradient-to-br from-teal-50 to-cyan-50 p-3 rounded-lg border border-teal-200 transition-transform hover:scale-105">
              <p className="text-[10px] font-semibold text-gray-600 mb-1 truncate" title={formattedKey}>{formattedKey}</p>
              <p className="text-lg font-bold text-teal-700">{displayValue}</p>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      <Toaster position="top-right" richColors />
      <div className="container mx-auto px-4 pt-24 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center gap-3"><Activity className="w-10 h-10 text-teal-600" />{t('nav.chronicDisease')}</h1>
            <p className="text-gray-600">{t('chronicDisease.trackAndManage')}</p>
          </div>

          <Card className="p-6 mb-6" data-testid="condition-selector">
            <h2 className="text-xl font-bold text-gray-800 mb-4">{t('chronicDisease.selectCondition')}</h2>
            <div className="flex flex-wrap gap-3">
              {conditions.map(c => (
                <Button key={c.id} onClick={() => { setActiveCondition(c.id); setShowAddLog(false); setPrediction(null); setReport(null); }} variant={activeCondition === c.id ? 'default' : 'outline'} className="flex items-center gap-2">
                  <span className="text-xl">{c.icon}</span>{c.name}
                </Button>
              ))}
            </div>
          </Card>

          {activeCondition && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-6">
                <Card className="p-6">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">{t('chronicDisease.quickActionsTitle')}</h3>
                  <div className="space-y-3">
                    <Button onClick={() => setShowAddLog(!showAddLog)} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="add-log-btn"><Plus className="w-4 h-4 mr-2" />{t('chronicDisease.addNewLog')}</Button>
                    <Button onClick={handlePredictFlare} disabled={loading || logs.length < 3} variant="outline" className="w-full border-orange-500 text-orange-600" data-testid="predict-flare-btn"><TrendingUp className="w-4 h-4 mr-2" />{t('chronicDisease.predictFlareButton')}</Button>
                    <Button onClick={handleGenerateReport} disabled={loading || logs.length === 0} variant="outline" className="w-full border-blue-500 text-blue-600" data-testid="generate-report-btn"><FileText className="w-4 h-4 mr-2" />{t('chronicDisease.generateReportButton')}</Button>
                  </div>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-100">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-600" />{t('chronicDisease.analytics')}</h3>
                    <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full border border-gray-200">Last 15 readings</span>
                  </div>
                  <ConditionCharts activeCondition={activeCondition} chartData={prepareChartData()} t={t} />
                </Card>

                <Card className="p-6 bg-gradient-to-br from-white to-teal-50 border-2 border-teal-100">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Activity className="w-5 h-5 text-teal-600" />30-Day Statistics</h3>
                    <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full border border-gray-200">Last 30 days</span>
                  </div>
                  {renderStatistics()}
                </Card>
              </div>

              <div className="lg:col-span-2 space-y-6">
                {showAddLog && (
                  <Card className="p-6" data-testid="add-log-form">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Add {conditions.find(c => c.id === activeCondition)?.name} Log</h3>
                    <ConditionLogForm
                      activeCondition={activeCondition}
                      forms={{ diabetesForm, hypertensionForm, asthmaForm, arthritisForm, ibsForm }}
                      setForms={{ setDiabetesForm, setHypertensionForm, setAsthmaForm, setArthritisForm, setIbsForm }}
                    />
                    <div className="flex gap-3 mt-6">
                      <Button onClick={handleAddLog} disabled={loading} className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="save-log-btn">{loading ? 'Saving...' : 'Save Log'}</Button>
                      <Button onClick={() => { setShowAddLog(false); resetForms(); }} variant="outline">Cancel</Button>
                    </div>
                  </Card>
                )}

                {prediction && (
                  <Card className="p-6 bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200" data-testid="flare-prediction">
                    <h3 className="text-xl font-bold text-orange-800 mb-4 flex items-center gap-2"><AlertCircle className="w-6 h-6" />Flare Risk Prediction</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                        <span className="font-semibold">Risk Level:</span>
                        <span className={`px-4 py-2 rounded-full font-bold ${prediction.risk_level === 'critical' ? 'bg-red-600 text-white' : prediction.risk_level === 'high' ? 'bg-orange-600 text-white' : prediction.risk_level === 'moderate' ? 'bg-yellow-500 text-white' : 'bg-green-600 text-white'}`}>{prediction.risk_level?.toUpperCase()}</span>
                      </div>
                      <div className="bg-white p-4 rounded-lg"><p className="font-semibold mb-2">Risk Score:</p><div className="flex items-center gap-3"><div className="flex-1 bg-gray-200 rounded-full h-4"><div className="bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 h-4 rounded-full" style={{ width: `${prediction.risk_score}%` }} /></div><span className="font-bold text-lg">{prediction.risk_score}/100</span></div></div>
                      <div className="bg-white p-4 rounded-lg"><p className="font-semibold mb-2">Predicted Timeframe:</p><p className="text-gray-700">{prediction.predicted_timeframe}</p></div>
                      {prediction.risk_factors?.length > 0 && <div className="bg-white p-4 rounded-lg"><p className="font-semibold mb-2">Risk Factors:</p><ul className="list-disc ml-5 text-gray-700 space-y-1">{prediction.risk_factors.map((f, i) => <li key={`rf-${i}`}>{f}</li>)}</ul></div>}
                      {prediction.recommendations?.length > 0 && <div className="bg-white p-4 rounded-lg"><p className="font-semibold mb-2">Recommendations:</p><ul className="list-disc ml-5 text-gray-700 space-y-1">{prediction.recommendations.map((r, i) => <li key={`rc-${i}`}>{r}</li>)}</ul></div>}
                      {prediction.warning_signs?.length > 0 && <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-300"><p className="font-semibold mb-2 text-yellow-800">Warning Signs to Watch:</p><ul className="list-disc ml-5 text-gray-700 space-y-1">{prediction.warning_signs.map((s, i) => <li key={`ws-${i}`}>{s}</li>)}</ul></div>}
                    </div>
                    <Button onClick={() => setPrediction(null)} variant="outline" className="w-full mt-4">Close Prediction</Button>
                  </Card>
                )}

                {report && (
                  <Card className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200" data-testid="doctor-report">
                    <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center gap-2"><FileText className="w-6 h-6" />Doctor Report (30 Days)</h3>
                    <div className="bg-white p-6 rounded-lg shadow-sm"><pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">{report}</pre></div>
                    <div className="flex gap-3 mt-4">
                      <Button onClick={() => { navigator.clipboard.writeText(report); toast.success('Report copied to clipboard!'); }} variant="outline" className="flex-1">Copy Report</Button>
                      <Button onClick={() => setReport(null)} variant="outline">Close</Button>
                    </div>
                  </Card>
                )}

                <Card className="p-6" data-testid="recent-logs">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center justify-between">
                    <span className="flex items-center gap-2"><Calendar className="w-5 h-5" />{t('chronicDisease.recentLogs')} ({logs.length})</span>
                  </h3>
                  {loading ? <p className="text-gray-500">Loading logs...</p> : logs.length === 0 ? <p className="text-gray-500">No logs yet. Add your first log to start tracking!</p> : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {logs.map(log => (
                        <div key={log.id} className="border rounded-lg p-4 hover:bg-gray-50">
                          <p className="text-sm text-gray-500 mb-2">{new Date(log.timestamp).toLocaleString()}</p>
                          <div className="text-sm space-y-1">
                            {Object.entries(log.log_data).map(([key, value]) => value && (
                              <p key={key} className="text-gray-700"><span className="font-semibold capitalize">{key.replace(/_/g, ' ')}:</span> {Array.isArray(value) ? value.join(', ') : String(value)}</p>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            </div>
          )}

          {!activeCondition && (
            <Card className="p-12 text-center"><Heart className="w-16 h-16 text-teal-600 mx-auto mb-4" /><h3 className="text-2xl font-bold text-gray-800 mb-2">Start Managing Your Health</h3><p className="text-gray-600 mb-6">Select a chronic condition above to begin tracking and get AI-powered insights</p></Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChronicDiseaseHub;
